import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AVATAR_NAME,
  AVATAR_FULL_NAME,
  OPENING_MESSAGE,
  CHIP_ROUNDS,
  KEYWORD_RESPONSES,
  FALLBACK_RESPONSE,
  FALLBACK_CAPTION,
  FAMILY_CHAT,
  IMPROVE_MODAL,
  SESSION_PAYOFF,
  MESSAGE_LIMIT,
  END_OF_DEMO,
  CHIPS_CAPTION,
  INPUT_PLACEHOLDER,
  TYPING_DELAY_MIN_MS,
  TYPING_DELAY_MAX_MS,
} from '../data/demoScript';
import './DemoPage.css';

// Quarantined interactive demo. No backend, no API calls — every response is
// scripted in ../data/demoScript.js. Deliberately imports nothing from the
// real chat components; the full-app layout (sidebar + top bar + chat) is
// replicated by hand in DemoPage.css. The only shared thing is the static
// /Sayagain.png logo asset from the public folder.

let nextId = 1;
const makeMsg = (sender, text, extra = {}) => ({ id: nextId++, sender, text, ...extra });

const pickReply = (text) => {
  const lower = text.toLowerCase();
  for (const entry of KEYWORD_RESPONSES) {
    if (entry.triggers.some((t) => lower.includes(t))) {
      return { text: entry.response, isFallback: false };
    }
  }
  return { text: FALLBACK_RESPONSE, isFallback: true };
};

const typingDelay = () =>
  TYPING_DELAY_MIN_MS + Math.floor(Math.random() * (TYPING_DELAY_MAX_MS - TYPING_DELAY_MIN_MS));

const initialsFor = (sender) => {
  if (sender === 'robert') return 'RM';
  if (sender === 'you') return 'You';
  return sender.substring(0, 2).toUpperCase();
};

const INERT_CHAT_TIP = 'Your conversations appear here';
const INERT_UI_TIP = 'Available in the full experience.';

const PRIVATE_CHAT_ENTRIES = [
  { key: 'chat', label: `Chat with ${AVATAR_NAME}`, active: true },
  { key: 'inert-lake', label: 'Saturday mornings at the lake', active: false },
  { key: 'inert-table', label: 'The kitchen table story', active: false },
  { key: 'inert-advice', label: "Dad's advice", active: false },
];

const GROUP_CHAT_ENTRIES = [
  { key: 'family', label: 'Family Chat', active: true },
  { key: 'inert-holidays', label: 'Mitchell Family — Holidays', active: false },
];

const SIDEBAR_MEMBERS = [
  { name: 'Robert Mitchell', initials: 'RM' },
  { name: 'Susan', initials: 'SU' },
  { name: 'Tommy', initials: 'TO' },
];

// Shared demo experience. `compact` renders the chat window only (no sidebar,
// tighter spacing) for embedding in the homepage hero; `expanded` + `onToggleExpand`
// support the hero's near-fullscreen overlay. The /demo page renders it full-size.
// One component, one state — the compact and full views can never drift apart.
export const DemoExperience = ({ compact = false, expanded = false, onToggleExpand = null }) => {
  const [activeScene, setActiveScene] = useState('chat'); // 'chat' | 'family'
  const [chatMessages, setChatMessages] = useState([makeMsg('robert', OPENING_MESSAGE)]);
  const [familyMessages, setFamilyMessages] = useState(
    FAMILY_CHAT.seedMessages.map((m) => makeMsg(m.sender.toLowerCase(), m.text))
  );
  const [typingIn, setTypingIn] = useState(null);
  const [visitorCount, setVisitorCount] = useState(0);
  const [captionUsed, setCaptionUsed] = useState(false);
  const [familyFirstReplied, setFamilyFirstReplied] = useState(false);
  const [demoEnded, setDemoEnded] = useState(false);
  const [showEndCard, setShowEndCard] = useState(false);
  // Rotating suggested-question chips: chips already used (each used once).
  const [usedChips, setUsedChips] = useState([]);
  // Members section: expanded on desktop, collapsed by default on mobile.
  const [membersExpanded, setMembersExpanded] = useState(
    () => typeof window === 'undefined' || window.innerWidth > 768
  );

  // Improve modal state
  const [improveOpen, setImproveOpen] = useState(false);
  const [improveThread, setImproveThread] = useState([]);
  const [improveStep, setImproveStep] = useState(0);
  const [improveInput, setImproveInput] = useState('');
  const [improveBusy, setImproveBusy] = useState(false);
  const [confidence, setConfidence] = useState(IMPROVE_MODAL.confidenceStart);
  const [payoffDelivered, setPayoffDelivered] = useState(false);
  // One-time expand hint (compact embed only): 'unshown' → 'shown' after the
  // first chip click → 'dismissed' after the next chip click or once used.
  const [expandHint, setExpandHint] = useState('unshown');

  const messagesRef = useRef(null);
  const improveThreadRef = useRef(null);
  const timersRef = useRef([]);

  const later = (fn, ms) => {
    const t = setTimeout(fn, ms);
    timersRef.current.push(t);
  };
  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  const messages = activeScene === 'chat' ? chatMessages : familyMessages;

  // Scroll ONLY the internal containers — never scrollIntoView/window.scrollTo,
  // which move the page when the demo is embedded partway down the homepage.
  // On first render, stay at the TOP so the opening message is fully visible.
  const messagesScrolledRef = useRef(false);
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    if (!messagesScrolledRef.current) {
      messagesScrolledRef.current = true;
      el.scrollTop = 0;
      return;
    }
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [chatMessages, familyMessages, typingIn, activeScene, showEndCard]);

  useEffect(() => {
    const el = improveThreadRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [improveThread, improveBusy, improveOpen]);

  const sendVisitorMessage = (rawText) => {
    const text = rawText.trim();
    if (!text || demoEnded || typingIn) return;

    const scene = activeScene;
    const append = scene === 'chat' ? setChatMessages : setFamilyMessages;
    append((prev) => [...prev, makeMsg('you', text)]);

    const newCount = visitorCount + 1;
    setVisitorCount(newCount);
    const isLast = newCount >= MESSAGE_LIMIT;
    if (isLast) setDemoEnded(true);

    const reply = pickReply(text);
    const withCaption = reply.isFallback && !captionUsed;
    if (withCaption) setCaptionUsed(true);

    const familyFirst = scene === 'family' && !familyFirstReplied;
    if (familyFirst) setFamilyFirstReplied(true);

    setTypingIn(scene);
    later(() => {
      setTypingIn(null);
      append((prev) => [
        ...prev,
        makeMsg('robert', reply.text, withCaption ? { caption: FALLBACK_CAPTION } : {}),
      ]);
      if (familyFirst) {
        later(() => {
          setFamilyMessages((prev) => [
            ...prev,
            makeMsg(FAMILY_CHAT.interjection.sender.toLowerCase(), FAMILY_CHAT.interjection.text),
          ]);
          if (isLast) later(() => setShowEndCard(true), 600);
        }, FAMILY_CHAT.interjectionDelayMs);
      } else if (isLast) {
        later(() => setShowEndCard(true), 600);
      }
    }, typingDelay());
  };

  // Chip click: send the message and retire the chip. Each chip is used
  // exactly once; its slot refills from the pool in round order.
  const handleChipClick = (chip) => {
    if (demoEnded || typingIn) return;
    sendVisitorMessage(chip);
    setUsedChips((prev) => [...prev, chip]);
    if (compact && onToggleExpand) {
      setExpandHint((h) => (h === 'unshown' ? 'shown' : h === 'shown' ? 'dismissed' : h));
    }
  };

  // Flat pool of all 12 chips in round order (lighter → more personal).
  // Always show the first three still-unused chips, so a used chip is
  // replaced immediately and exactly three stay on screen until fewer
  // than three remain unused.
  const chipPool = CHIP_ROUNDS.flat();
  const visibleChips = chipPool.filter((c) => !usedChips.includes(c)).slice(0, 3);

  // ── Improve modal logic ──────────────────────────────────────────
  const openImprove = () => {
    setImproveOpen(true);
    if (improveThread.length === 0) {
      setImproveThread([makeMsg('robert', IMPROVE_MODAL.questions[0].question)]);
    }
  };

  const closeImprove = () => {
    setImproveOpen(false);
    // One-time payoff once Q1 and Q2 have been answered. The greeting is a
    // fixed line — visitor answers are never inserted into Robert's replies.
    if (improveStep >= 2 && !payoffDelivered) {
      setPayoffDelivered(true);
      setTypingIn('chat');
      later(() => {
        setTypingIn(null);
        setChatMessages((prev) => [...prev, makeMsg('robert', SESSION_PAYOFF)]);
      }, typingDelay());
    }
  };

  const submitImproveAnswer = (e) => {
    e.preventDefault();
    const answer = improveInput.trim();
    if (!answer || improveBusy || improveStep >= IMPROVE_MODAL.questions.length) return;

    const q = IMPROVE_MODAL.questions[improveStep];
    setImproveThread((prev) => [...prev, makeMsg('you', answer)]);
    setImproveInput('');
    setImproveBusy(true);

    later(() => {
      setImproveThread((prev) => [...prev, makeMsg('robert', q.ack)]);
      setConfidence((c) => Math.min(c + IMPROVE_MODAL.confidencePerAnswer, IMPROVE_MODAL.confidenceCap));
      const nextStep = improveStep + 1;
      setImproveStep(nextStep);
      if (nextStep < IMPROVE_MODAL.questions.length) {
        later(() => {
          setImproveThread((prev) => [
            ...prev,
            makeMsg('robert', IMPROVE_MODAL.questions[nextStep].question),
          ]);
          setImproveBusy(false);
        }, 900);
      } else {
        setImproveBusy(false);
      }
    }, typingDelay());
  };

  const improveDone = improveStep >= IMPROVE_MODAL.questions.length;

  const renderSidebarEntry = (entry, section) => {
    if (entry.active) {
      const isCurrent =
        (entry.key === 'chat' && activeScene === 'chat') ||
        (entry.key === 'family' && activeScene === 'family');
      return (
        <div
          key={entry.key}
          className={`demo-history-item ${isCurrent ? 'active' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => setActiveScene(entry.key)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setActiveScene(entry.key);
          }}
        >
          {section === 'group' ? (
            <div className="demo-list-icon gradient-teal-demo">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
          ) : (
            <span className="demo-history-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </span>
          )}
          <div className="demo-history-title">{entry.label}</div>
        </div>
      );
    }
    return (
      <div key={entry.key} className="demo-history-item demo-inert-item" data-tip={INERT_CHAT_TIP}>
        {section === 'group' ? (
          <div className="demo-list-icon gradient-teal-demo">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
        ) : (
          <span className="demo-history-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
        )}
        <div className="demo-history-title">{entry.label}</div>
      </div>
    );
  };

  return (
    <div
      className={`demo-page${compact ? ' demo-page--compact' : ''}${expanded ? ' demo-page--expanded' : ''}`}
    >
      {/* ── Sidebar (replica of .chat-sidebar, inert except the two scenes) — hidden in compact ── */}
      {!compact && (
      <aside className="demo-sidebar">
        <div className="demo-sidebar-header">
          <Link to="/" className="demo-sidebar-logo">
            <img
              src={process.env.PUBLIC_URL + '/Sayagain.png'}
              alt="SayAgain"
              className="demo-sidebar-logo-img"
            />
          </Link>
        </div>

        <span className="demo-inert-wrap demo-newchat-wrap" data-tip={INERT_UI_TIP}>
          <button type="button" className="demo-new-chat-btn" disabled>
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            New Chat
          </button>
        </span>

        <div className="demo-sidebar-scroll">
          <div className="demo-history-label">Private Chats</div>
          <div className="demo-history-list">
            {PRIVATE_CHAT_ENTRIES.map((e) => renderSidebarEntry(e, 'private'))}
          </div>

          <div className="demo-history-label demo-history-label-group">Group Chats</div>
          <div className="demo-history-list">
            {GROUP_CHAT_ENTRIES.map((e) => renderSidebarEntry(e, 'group'))}
          </div>
        </div>

        <div className="demo-sidebar-members">
          <button
            type="button"
            className="demo-members-toggle"
            onClick={() => setMembersExpanded((p) => !p)}
            title={membersExpanded ? 'Collapse members' : 'Show members'}
          >
            <span className="demo-members-label">Members</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {membersExpanded ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              )}
            </svg>
          </button>
          {membersExpanded && (
            <div className="demo-members-list">
              {SIDEBAR_MEMBERS.map((m) => (
                <div key={m.name} className="demo-member-row demo-inert-member" data-tip={INERT_CHAT_TIP}>
                  <div className="demo-member-initials">{m.initials}</div>
                  <div className="demo-member-name">{m.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
      )}

      {/* ── Main column (replica of .chat-main) ── */}
      <div className="demo-main">
        <header className="demo-header">
          <div className="demo-header-left">
            <div className="demo-header-info">
              <h3>
                <span>{activeScene === 'chat' ? AVATAR_FULL_NAME : 'Family Chat'}</span>
                {activeScene === 'chat' && (
                  <button
                    type="button"
                    className="demo-icon-btn-inline"
                    title="Improve Avatar"
                    onClick={openImprove}
                  >
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                  </button>
                )}
              </h3>
              <p>Interactive demo — responses are scripted</p>
            </div>
          </div>
          <div className="demo-header-actions">
            {onToggleExpand && (
              <button
                type="button"
                className={`demo-expand-btn${expanded ? '' : ' demo-expand-btn--labeled'}`}
                aria-label={expanded ? 'Close expanded demo' : 'Expand demo'}
                title={expanded ? 'Close' : 'Expand'}
                onClick={onToggleExpand}
              >
                {expanded ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                    </svg>
                    <span className="demo-expand-btn-label">Expand</span>
                  </>
                )}
              </button>
            )}
            <span className="demo-inert-wrap demo-tip-left" data-tip={INERT_UI_TIP}>
              <button type="button" className="demo-profile-circle" aria-disabled="true" tabIndex={-1}>
                You
              </button>
            </span>
          </div>
        </header>

        <div className="demo-messages" ref={messagesRef}>
          {messages.map((m) => {
            const own = m.sender === 'you';
            const robert = m.sender === 'robert';
            return (
              <div key={m.id} className={`demo-message ${own ? 'own' : ''}`}>
                <div
                  className={`demo-msg-avatar ${
                    own ? 'gradient-gold-demo' : robert ? 'gradient-sage-demo' : 'gradient-teal-demo'
                  }`}
                >
                  {initialsFor(m.sender)}
                </div>
                <div className="demo-msg-body">
                  {!own && !robert && (
                    <div className="demo-sender-name">
                      {m.sender.charAt(0).toUpperCase() + m.sender.slice(1)}
                    </div>
                  )}
                  <div className={`demo-msg-content ${own ? 'own' : ''}`}>{m.text}</div>
                  {m.caption && <div className="demo-msg-caption">{m.caption}</div>}
                </div>
              </div>
            );
          })}
          {typingIn === activeScene && (
            <div className="demo-message">
              <div className="demo-msg-avatar gradient-sage-demo">RM</div>
              <div className="demo-msg-body">
                <div className="demo-msg-content demo-typing">
                  <span className="demo-dot" />
                  <span className="demo-dot" />
                  <span className="demo-dot" />
                </div>
              </div>
            </div>
          )}

          {showEndCard && (
            <div className="demo-end-card">
              <h2>{END_OF_DEMO.heading}</h2>
              <p>{END_OF_DEMO.body}</p>
              <Link to={END_OF_DEMO.buttonLink} className="demo-end-cta">
                {END_OF_DEMO.buttonLabel}
              </Link>
            </div>
          )}
        </div>

        {!demoEnded && (
          <div className="demo-improve-invite">
            <button type="button" className="demo-improve-invite-link" onClick={openImprove}>
              Help {AVATAR_NAME} remember
            </button>
          </div>
        )}

        <div className="demo-input-area">
          {!demoEnded && (
            <>
              {compact && onToggleExpand && expandHint === 'shown' && (
                <div className="demo-expand-hint">
                  Easier to read in a bigger window —{' '}
                  <button
                    type="button"
                    className="demo-expand-hint-link"
                    onClick={() => {
                      setExpandHint('dismissed');
                      onToggleExpand();
                    }}
                  >
                    expand the demo
                  </button>
                </div>
              )}
              <div className="demo-chips-caption">{CHIPS_CAPTION}</div>
              <div className="demo-chips">
                {visibleChips.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    className="demo-chip"
                    disabled={!!typingIn}
                    onClick={() => handleChipClick(chip)}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </>
          )}
          {/* Input is visible but non-interactive in the demo — chips drive the conversation */}
          <div className="demo-input-wrapper">
            <input
              type="text"
              className="demo-input"
              placeholder={INPUT_PLACEHOLDER}
              value=""
              disabled
              readOnly
              aria-label={INPUT_PLACEHOLDER}
            />
            <button type="button" className="demo-send-btn" disabled aria-label="Send">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13" />
                <path d="M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Improve modal */}
      {improveOpen && (
        <div className="demo-modal-overlay" onClick={closeImprove}>
          <div
            className="demo-modal"
            role="dialog"
            aria-modal="true"
            aria-label={IMPROVE_MODAL.title}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="demo-modal-close" onClick={closeImprove} aria-label="Close">
              ×
            </button>
            <h2 className="demo-modal-title">{IMPROVE_MODAL.title}</h2>

            <div className="demo-confidence-header">
              <span className="demo-confidence-label">{IMPROVE_MODAL.confidenceLabel}</span>
              <span className="demo-confidence-score">{confidence}%</span>
            </div>
            <div className="demo-confidence-bar">
              <div className="demo-confidence-fill" style={{ width: `${confidence}%` }} />
            </div>

            <div className="demo-improve-stats">
              {IMPROVE_MODAL.stats.map((s) => (
                <div key={s.label} className="demo-improve-stat">
                  <div className="demo-improve-stat-value">{s.value}</div>
                  <div className="demo-improve-stat-label">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="demo-improve-thread" ref={improveThreadRef}>
              {improveThread.map((m) => (
                <div
                  key={m.id}
                  className={`demo-improve-msg ${m.sender === 'you' ? 'from-you' : 'from-robert'}`}
                >
                  {m.text}
                </div>
              ))}
              {improveBusy && (
                <div className="demo-improve-msg from-robert demo-typing">
                  <span className="demo-dot" />
                  <span className="demo-dot" />
                  <span className="demo-dot" />
                </div>
              )}
            </div>

            {!improveDone ? (
              <form className="demo-improve-form" onSubmit={submitImproveAnswer}>
                <input
                  type="text"
                  className="demo-improve-input"
                  placeholder="Type your answer…"
                  value={improveInput}
                  onChange={(e) => setImproveInput(e.target.value)}
                  disabled={improveBusy}
                  aria-label="Your answer"
                />
                <button type="submit" className="demo-improve-send" disabled={improveBusy} aria-label="Send answer">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <path d="M22 2L11 13" />
                    <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                </button>
              </form>
            ) : (
              <button type="button" className="demo-improve-done" onClick={closeImprove}>
                Back to the conversation
              </button>
            )}

            {/* Inert add-documents control (mirrors the real improve modal's
                .improve-add-docs button; label and styling replicated) */}
            <div className="demo-improve-add-docs">
              <span className="demo-inert-wrap demo-add-docs-wrap" data-tip={INERT_UI_TIP}>
                <button type="button" className="demo-add-docs-btn" aria-disabled="true" tabIndex={-1}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add more documents
                </button>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// The standalone /demo route: the full experience, no expand control.
const DemoPage = () => <DemoExperience />;

export default DemoPage;
